<template>
  <div class="min-h-screen bg-cream select-none">

    <!-- Загрузка -->
    <div v-if="loading" class="flex justify-center pt-24">
      <div class="w-6 h-6 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
    </div>

    <div v-else-if="dish" class="px-5 pt-4 pb-8">

      <!-- Назад -->
      <button @click="goBack" class="mb-3 text-ink active:opacity-60">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <!-- Название блюда (редактируемое) -->
      <div class="flex items-center gap-2 mb-4">
        <template v-if="editingName">
          <input
            ref="nameInputRef"
            v-model="dishName"
            @blur="editingName = false"
            @keydown.enter="editingName = false"
            class="flex-1 bg-transparent border-b border-t-0 border-x-0 border-[#C4B8A8] outline-none"
            style="font-family: Inter, sans-serif; font-size: var(--fs-22); font-weight: 700; color: var(--ink);"
          />
        </template>
        <template v-else>
          <h1 style="font-family: Inter, sans-serif; font-size: var(--fs-22); font-weight: 700; color: var(--ink); line-height: 1.2;">
            {{ dishName }}
          </h1>
          <button @click="startEditName" class="text-ink/60 active:text-ink transition-colors shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
            </svg>
          </button>
        </template>
      </div>

      <!-- Фото (кликабельно для загрузки) -->
      <div class="relative w-[200px] rounded-2xl overflow-hidden cursor-pointer mb-4" style="aspect-ratio: 1/1.1;" @click="showPhotoMenu = true">
        <img v-if="photoUrl" :src="imgUrl(photoUrl)" alt="" class="w-full h-full object-cover" />
        <div v-else class="w-full h-full bg-[#F0EBE2] flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C4B8A8" stroke-width="1.5" stroke-linecap="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
        </div>
        <div class="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors">
          <div v-if="uploadingPhoto" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        </div>
        <!-- Суммарные КБЖУ пилюли -->
        <div class="absolute top-1.5 left-1.5 flex flex-col gap-0.5 items-start">
          <span class="pill-kcal text-9 font-semibold">{{ totalKcal }}</span>
          <span class="pill-protein text-9">{{ totalProtein }}</span>
          <span class="pill-fat text-9">{{ totalFat }}</span>
          <span class="pill-carbs text-9">{{ totalCarbs }}</span>
          <span v-if="totalFiber != null" class="pill-fiber text-9">{{ totalFiber }}</span>
        </div>
        <!-- Сердечко -->
        <button
          @click.stop="toggleFavorite"
          class="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
        >
          <svg width="14" height="12" viewBox="0 0 24 20" :fill="isFavorite ? '#FF6B6B' : 'none'" :stroke="isFavorite ? '#FF6B6B' : 'white'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M13.8131 18.2326C12.5923 19.442 10.7015 19.442 9.48071 18.2326L2.19973 11.019C-2.67113 6.14805 3.68934 -4.6136 11.6469 3.34396C19.5905 -4.59961 25.951 6.16221 21.0941 11.019L13.8131 18.2326Z"/>
          </svg>
        </button>
      </div>

      <!-- Теги блюда -->
      <DishTagsEditor :model-value="dishTags" @update:model-value="saveDishTags" class="mb-5" />

      <!-- КБЖУ на 100 г готового блюда -->
      <div class="mb-5">
        <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);" class="mb-2">КБЖУ на 100 г готового блюда:</p>
        <div class="flex flex-wrap gap-2">
          <span class="pill-big pill-kcal">{{ per100Kcal }} ккал</span>
          <span class="pill-big pill-protein">{{ per100Protein }} г</span>
          <span class="pill-big pill-fat">{{ per100Fat }} г</span>
          <span class="pill-big pill-carbs">{{ per100Carbs }} г</span>
          <span v-if="per100Fiber != null" class="pill-big pill-fiber">{{ per100Fiber }} г</span>
        </div>
      </div>

      <!-- Основной способ готовки -->
      <div class="mb-4">
        <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);" class="mb-2">Основной способ готовки:</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="m in cookingMethods"
            :key="m.value"
            @click="cookingMethod = m.value"
            class="px-3 py-1.5 rounded-full text-13 font-semibold transition-colors"
            :class="cookingMethod === m.value ? 'bg-ink text-cream' : 'bg-[#F0EBE2] text-ink'"
          >{{ m.label }}</button>
        </div>
      </div>

      <!-- Ингредиенты -->
      <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);" class="mb-2">Ингредиенты:</p>
      <div class="mb-4">
        <div
          v-for="(item, idx) in items"
          :key="idx"
          class="py-3"
          :style="idx < items.length - 1 ? 'border-bottom: 1px solid #EDE5D8;' : ''"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <!-- Название (кликабельно для замены) -->
              <button @click="openSearchForItem(idx)" class="text-left">
                <p class="text-15 font-semibold text-ink leading-snug">{{ item.product_name }}</p>
              </button>
              <!-- db_name -->
              <p v-if="item.db_product_name" class="text-12 text-ink/70 mt-0.5">→ {{ item.db_product_name }}</p>
              <!-- КБЖУ на 100г ингредиента -->
              <div v-if="item.per100" class="flex flex-wrap items-center gap-1 mt-1.5">
                <span class="pill-kcal text-10">{{ Math.round(item.per100.kcal || 0) }} ккал</span>
                <span class="pill-protein text-10">{{ Math.round(item.per100.protein || 0) }} г</span>
                <span class="pill-fat text-10">{{ Math.round(item.per100.fat || 0) }} г</span>
                <span class="pill-carbs text-10">{{ Math.round(item.per100.carbs || 0) }} г</span>
                <!-- Клетчатки может не быть ни в карточке продукта, ни в
                     оценке. Ноль здесь читался бы как измеренный ноль. -->
                <span v-if="item.per100.fiber != null" class="pill-fiber text-10">{{ fmtFiber(Number(item.per100.fiber)) }} г</span>
                <span
                  v-if="getSourceBadge(item.product_source)"
                  class="text-11 font-medium ml-1"
                  :style="{ color: getSourceBadge(item.product_source).color }"
                >✓ {{ getSourceBadge(item.product_source).label }}</span>
              </div>
            </div>
            <!-- Вес + удалить -->
            <div class="flex items-center gap-1 shrink-0">
              <template v-if="showWeight">
                <button
                  v-if="!item._editingWeight"
                  @click="startEditItemWeight(idx)"
                  class="text-14 font-semibold text-ink bg-white rounded-lg px-2.5 py-1"
                >{{ Math.round(item.weight_g || 0) }} г</button>
                <input
                  v-else
                  :ref="el => { if (el) itemWeightRefs[idx] = el }"
                  :value="item.weight_g"
                  @input="item.weight_g = parseDecimal($event.target.value)"
                  type="text"
                  inputmode="decimal"
                  @blur="finishEditItemWeight(idx)"
                  @keydown.enter="finishEditItemWeight(idx)"
                  class="w-16 text-right text-14 font-semibold text-ink bg-white rounded-lg px-2.5 py-1 border border-[#C4B8A8] outline-none"
                />
              </template>
              <button @click="removeItem(idx)" class="text-ink/60 active:text-error ml-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- + Добавить продукт из базы данных -->
      <button @click="openSearchForAdd" class="text-15 text-ink mb-5 active:opacity-60">
        + Добавить продукт из базы данных
      </button>

      <!-- Итоговый вес готового блюда -->
      <div v-if="showWeight" class="mb-5">
        <div class="flex items-center justify-between">
          <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);">Итоговый вес готового блюда:</p>
          <button
            v-if="!editingWeight"
            @click="startEditWeight"
            class="text-16 font-semibold text-ink bg-white rounded-lg px-3 py-1.5"
          >{{ Math.round(cookedWeight || 0) }} г</button>
          <input
            v-else
            ref="weightInputRef"
            :value="cookedWeight"
            @input="onCookedWeightInput"
            type="text"
            inputmode="decimal"
            @blur="finishEditWeight"
            @keydown.enter="finishEditWeight"
            class="w-24 text-right text-16 font-semibold text-ink bg-white rounded-lg px-3 py-1.5 border border-[#C4B8A8] outline-none"
          />
        </div>
        <p v-if="weightMismatchHint" class="text-12 text-ink/70 mt-1.5">{{ weightMismatchHint }}</p>

        <!-- Порций на рецепт: вес порции = вес блюда / порции. При добавлении
             из рецепта в дневник по умолчанию берётся одна порция. -->
        <div class="flex items-center justify-between mt-3">
          <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);">Порций на рецепт:</p>
          <div class="flex items-center gap-1">
            <button @click="stepPortions(-1)" class="w-8 h-8 rounded-lg bg-white text-ink text-18 font-semibold active:bg-line">−</button>
            <input
              :value="portionsStr"
              @input="onPortionsInput"
              @blur="finishPortions"
              type="text" inputmode="decimal" placeholder="—"
              class="w-14 text-center text-16 font-semibold text-ink bg-white rounded-lg px-2 py-1.5 outline-none"
            />
            <button @click="stepPortions(1)" class="w-8 h-8 rounded-lg bg-white text-ink text-18 font-semibold active:bg-line">+</button>
          </div>
        </div>
        <p v-if="portionsCount > 0 && cookedWeight > 0" class="text-12 text-ink/70 mt-1.5">1 порция ≈ {{ Math.round(cookedWeight / portionsCount) }} г</p>
      </div>

      <!-- + Добавить текст рецепта -->
      <div class="mb-6">
        <button v-if="!showRecipeText && !recipeText" @click="showRecipeText = true" class="text-15 text-ink active:opacity-60">
          + Добавить текст рецепта
        </button>
        <div v-else>
          <textarea
            ref="recipeTextRef"
            v-model="recipeText"
            rows="3"
            placeholder="Опишите рецепт…"
            @blur="saveRecipeText"
            @input="autoResizeRecipeText"
            class="w-full rounded-xl px-3 py-2.5 text-14 text-ink outline-none resize-none placeholder-recipe"
            style="background: #FEEDB0; font-family: Inter, sans-serif;"
          ></textarea>
        </div>
      </div>

      <!-- Кнопки -->
      <button
        @click="saveChanges"
        :disabled="saving || (hasChanges === false && isEditedCooking)"
        class="w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold
               disabled:opacity-40 active:scale-[0.98] transition-all mb-3"
      >
        {{ saving ? 'Сохранение...' : 'Сохранить в Мои рецепты' }}
      </button>

      <button
        v-if="originalItems.length > 0"
        @click="removeFromRecipes"
        class="w-full py-3.5 rounded-2xl text-16 font-semibold bg-white active:scale-[0.98] transition-all"
        style="color: var(--error);"
      >
        Удалить из рецептов
      </button>
    </div>

    <!-- Hidden file inputs для фото -->
    <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onPhotoFile" />
    <input ref="galleryInput" type="file" accept="image/*" class="hidden" @change="onPhotoFile" />

    <!-- Попап: выбор источника фото -->
    <Teleport to="body">
      <div v-if="showPhotoMenu" class="fixed inset-0 z-[100] flex items-end justify-center" @click.self="showPhotoMenu = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]" @click="showPhotoMenu = false" />
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl shadow-2xl px-5 pt-6 pb-sheet-8">
          <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);" class="mb-5">Добавить фото рецепта</p>
          <button
            @click="showPhotoMenu = false; cameraInput?.click()"
            class="w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold active:scale-[0.98] transition-all mb-3 flex items-center justify-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
            </svg>
            Сделать фото
          </button>
          <button
            @click="showPhotoMenu = false; galleryInput?.click()"
            class="w-full py-3 rounded-2xl bg-white text-ink text-15 font-semibold active:scale-[0.98] transition-all mb-3 flex items-center justify-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V1.5C13.5 0.947715 13.0523 0.5 12.5 0.5H1.5C0.947715 0.5 0.5 0.947715 0.5 1.5V12.5C0.5 13.0523 0.947715 13.5 1.5 13.5Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1.75 13.5L9.05 6.61996C9.13984 6.54513 9.25307 6.50415 9.37 6.50415C9.48692 6.50415 9.60015 6.54513 9.68999 6.61996L13.5 9.34996" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.5 6C5.32843 6 6 5.32843 6 4.5C6 3.67157 5.32843 3 4.5 3C3.67157 3 3 3.67157 3 4.5C3 5.32843 3.67157 6 4.5 6Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Выбрать из галереи
          </button>
          <button
            @click="showPhotoMenu = false"
            class="w-full py-3 rounded-2xl text-ink/70 text-15 font-semibold"
          >Отмена</button>
        </div>
      </div>
    </Teleport>

    <!-- Попап: добавить воду -->
    <Teleport to="body">
      <div v-if="showWaterPopup" class="fixed inset-0 z-[100] flex items-end justify-center" @click.self="showWaterPopup = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]" @click="showWaterPopup = false" />
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl shadow-2xl px-5 pt-6 pb-sheet-8">
          <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink); line-height: 1.4;" class="mb-5">
            Итоговый вес блюда получился больше суммы ингредиентов. Почти всегда — это добавленная вода.<br><br>
            Добавить в список ингредиентов воду на разницу в весе ({{ waterDiff }} г)?
          </p>
          <button
            @click="addWaterAndSave"
            class="w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold active:scale-[0.98] transition-all mb-3"
          >
            Добавить воду и сохранить
          </button>
          <button
            @click="showWaterPopup = false"
            class="w-full py-3 rounded-2xl bg-white text-ink text-15 font-semibold active:scale-[0.98] transition-all"
          >
            Отмена
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Поиск продукта -->
    <Teleport to="body">
      <ProductSearchModal
        v-if="showSearchModal"
        :initial-query="searchModalQuery"
        :title="searchTarget?.type === 'add' ? 'Добавить продукт' : 'Заменить продукт'"
        @select="onSearchSelect"
        @close="showSearchModal = false"
      />
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '../services/api.js'
import { imgUrl } from '../utils/image.js'
import ProductSearchModal from '../components/ProductSearchModal.vue'
import DishTagsEditor from '../components/DishTagsEditor.vue'
import { useFavTagsStore } from '../store/favTags.js'
import { useAuthStore } from '../store/auth.js'
import { isCookedWeightImplausible } from '../utils/cookedWeight.js'
import { flattenRecipeIngredients } from '../utils/flattenRecipe.js'

const authStore = useAuthStore()
const showWeight = computed(() => {
  const ds = authStore.user?.diary_settings || {}
  return ds.show_weight ?? true
})

const route = useRoute()
const router = useRouter()
const dishId = route.params.id

function parseDecimal(str) {
  const num = parseFloat(String(str).replace(',', '.'))
  return isNaN(num) ? 0 : num
}

const loading = ref(true)
const saving = ref(false)
const dish = ref(null)
const items = ref([])
const dishName = ref('')
const isFavorite = ref(false)
const isEditedCooking = ref(false)
const cookedWeight = ref(0)
// Порций на рецепт (zoya_dishes.portions_count); пусто — не задано
const portionsCount = ref(0)
const portionsStr = ref('')
const originalPortions = ref(0)
function onPortionsInput(e) {
  portionsStr.value = e.target.value
  const n = parseFloat(String(e.target.value).replace(',', '.'))
  portionsCount.value = Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0
}
function finishPortions() { portionsStr.value = portionsCount.value > 0 ? String(portionsCount.value) : '' }
function stepPortions(d) {
  const n = Math.max(0, Math.round((portionsCount.value || 0) + d))
  portionsCount.value = n
  portionsStr.value = n > 0 ? String(n) : ''
}
const recipeText = ref('')
const showRecipeText = ref(false)
const cookingMethod = ref('raw')

const cookingMethods = [
  { value: 'raw', label: 'Сырой' },
  { value: 'boiling', label: 'Варка' },
  { value: 'frying', label: 'Жарка' },
  { value: 'baking', label: 'Запекание' },
  { value: 'steaming', label: 'На пару' },
  { value: 'stewing', label: 'Тушение' },
]

// Editing states
const editingName = ref(false)
const nameInputRef = ref(null)
const editingWeight = ref(false)
const weightInputRef = ref(null)
const itemWeightRefs = ref({})

// Original state for hasChanges tracking
const originalName = ref('')
const originalWeight = ref(0)
const originalCookingMethod = ref('raw')
const originalItems = ref([])

// Photo
const photoUrl = ref(null)
const showPhotoMenu = ref(false)
const uploadingPhoto = ref(false)
const cameraInput = ref(null)
const galleryInput = ref(null)

// Water popup
const showWaterPopup = ref(false)
// Авто-обновление cookedWeight при изменении ингредиентов (для новых рецептов)
const userEditedWeight = ref(false)
// Флаг: рецепт был пустым при загрузке (только что создан)
const isNewUnsaved = ref(false)

// Search modal
const showSearchModal = ref(false)
const searchModalQuery = ref('')
const searchTarget = ref(null) // { type: 'replace', idx } | { type: 'add' }

// ── Теги Избранного ──
const dishTags = ref([])
async function saveDishTags(list) {
  const prev = dishTags.value
  dishTags.value = list
  try { dishTags.value = await useFavTagsStore().setDishTags(dishId, list) }
  catch { dishTags.value = prev }
}

// ── Загрузка данных ──
onMounted(async () => {
  try {
    const { data } = await api.get(`/cooking/dish/${dishId}`)
    dish.value = data.dish
    dishName.value = data.dish.name
    isFavorite.value = data.dish.is_favorite
    dishTags.value = data.dish.tags || []
    isEditedCooking.value = data.dish.is_edited_cooking
    photoUrl.value = data.dish.photo_url || null
    cookedWeight.value = Number(data.dish.cooked_weight_g) || 0
    portionsCount.value = Number(data.dish.portions_count) || 0
    originalPortions.value = portionsCount.value
    portionsStr.value = portionsCount.value > 0 ? String(portionsCount.value) : ''
    recipeText.value = data.dish.recipe_text || ''
    showRecipeText.value = !!recipeText.value
    cookingMethod.value = data.dish.cooking_method || 'raw'

    // Рецепт — один уровень: позиция с раскладкой («Плов» из распознавания)
    // разворачивается в свои ингредиенты, см. flattenRecipe.js
    const source = data.recipe_ingredients
      ? flattenRecipeIngredients(data.recipe_ingredients)
      : data.entries
    items.value = source.map(e => {
      const per100 = e.per100 || computePer100(e)
      return {
        ...e,
        weight_g: Number(e.weight_g) || 0,
        kcal: Number(e.kcal) || 0,
        protein: Number(e.protein) || 0,
        fat: Number(e.fat) || 0,
        carbs: Number(e.carbs) || 0,
        fiber: e.fiber == null ? null : Number(e.fiber),
        per100,
        _editingWeight: false,
        _prevWeight: e.weight_g,
      }
    })

    // Если cooked_weight не задан, считаем как сумму
    const storedWeight = cookedWeight.value
    const ingTotal = ingredientsTotalWeight.value
    let weightRepaired = false
    if (!cookedWeight.value) {
      cookedWeight.value = ingTotal
    } else if (isCookedWeightImplausible(storedWeight, ingTotal)) {
      // Ужаркой пятикратная разница не объясняется — значит вес когда-то был
      // введён с ошибкой и сохранился. Считать его «заданным вручную» нельзя:
      // тогда авто-сумма молчит, и число не исправится уже никогда, при каждом
      // открытии запирая себя заново.
      //
      // Проверка живёт только здесь, на экране рецепта: тут вес описывает всё
      // готовое блюдо. В дневнике тот же рецепт — шаблон, и вес там означает
      // порцию, которая законно бывает в разы меньше состава.
      cookedWeight.value = Math.round(ingTotal)
      weightRepaired = true
    } else if (Math.abs(cookedWeight.value - ingTotal) > 1) {
      // Существующий рецепт с отличающимся весом — пользователь его задавал вручную
      userEditedWeight.value = true
    }

    // Сохраняем оригинальное состояние для отслеживания изменений
    originalName.value = dishName.value
    // Починенный вес держим за изменение: иначе кнопка сохранения останется
    // выключенной и исправление не доедет до базы.
    originalWeight.value = weightRepaired ? storedWeight : cookedWeight.value
    originalCookingMethod.value = cookingMethod.value
    originalItems.value = items.value.map(i => ({ product_name: i.product_name, weight_g: i.weight_g, product_id: i.product_id }))

    // Новый рецепт — автофокус на название + пометка для удаления при отмене
    if (dishName.value === 'Новый рецепт' && originalItems.value.length === 0) {
      isNewUnsaved.value = true
      editingName.value = true
      nextTick(() => nameInputRef.value?.focus())
    }
  } catch (err) {
    console.error('DishPage load error:', err)
  } finally {
    loading.value = false
  }
})

// ── Вычисления ──
function computePer100(entry) {
  const w = entry.weight_g || 100
  if (entry.product_nutrients) {
    // Из nutrients JSONB (Скурихин формат)
    const n = entry.product_nutrients
    const get = (...keys) => {
      for (const k of keys) {
        const raw = n[k]
        if (raw === undefined || raw === null) continue
        if (typeof raw === 'object' && raw.val != null) return parseFloat(raw.val)
        if (typeof raw === 'number') return raw
        const parsed = parseFloat(raw)
        if (!isNaN(parsed)) return parsed
      }
      return null
    }
    return {
      kcal: get('Калорийность', 'Энергетическая ценность', 'energy', 'calories'),
      protein: get('Белки', 'protein', 'proteins'),
      fat: get('Жиры', 'fat', 'fats', 'total_fat'),
      carbs: get('Углеводы', 'carbs', 'carbohydrates', 'total_carbohydrate'),
      // Ключи те же, что в FIBER_KEYS на сервере: источники называют
      // клетчатку по-разному. get отдаёт null, если её нет ни под одним.
      fiber: get('Пищевые волокна', 'dietary_fiber', 'fiber', 'Клетчатка'),
    }
  }
  // Вычисляем из абсолютных значений
  if (entry.kcal && w > 0) {
    return {
      kcal: Math.round((entry.kcal || 0) / w * 100 * 10) / 10,
      protein: Math.round((entry.protein || 0) / w * 100 * 10) / 10,
      fat: Math.round((entry.fat || 0) / w * 100 * 10) / 10,
      carbs: Math.round((entry.carbs || 0) / w * 100 * 10) / 10,
      fiber: entry.fiber == null ? null : Math.round(Number(entry.fiber) / w * 100 * 10) / 10,
    }
  }
  return null
}

const ingredientsTotalWeight = computed(() =>
  items.value.reduce((s, i) => s + (i.weight_g || 0), 0)
)

const waterDiff = computed(() =>
  Math.round((cookedWeight.value || 0) - ingredientsTotalWeight.value)
)

// Вес готового блюда законно отличается от суммы ингредиентов — вода
// выпаривается, крупа разбухает. Но видеть, от чего он отличается, человек
// должен: незамеченное расхождение и уезжает потом в дневник как вес порции.
const weightMismatchHint = computed(() => {
  const total = ingredientsTotalWeight.value
  if (!total || Math.abs((cookedWeight.value || 0) - total) <= 1) return ''
  return `Сумма ингредиентов — ${Math.round(total)} г`
})

// Авто-обновление cookedWeight при изменении ингредиентов
watch(ingredientsTotalWeight, (newTotal) => {
  if (!userEditedWeight.value && newTotal > 0) {
    cookedWeight.value = Math.round(newTotal)
  }
})

const totalKcal = computed(() => Math.round(items.value.reduce((s, i) => s + (i.kcal || 0), 0)))
const totalProtein = computed(() => Math.round(items.value.reduce((s, i) => s + (i.protein || 0), 0)))
const totalFat = computed(() => Math.round(items.value.reduce((s, i) => s + (i.fat || 0), 0)))
const totalCarbs = computed(() => Math.round(items.value.reduce((s, i) => s + (i.carbs || 0), 0)))
// Клетчатка считается не как остальные макросы. У части ингредиентов её нет
// ни в одном источнике, и такой ингредиент обязан выпасть из суммы, а не
// добавить в неё ноль. Если её нет ни у одного — null, и пилюли нет вовсе:
// ноль в этом месте читался бы как «клетчатки в блюде нет».
const fmtFiber = v => v >= 10 ? Math.round(v) : Math.round(v * 10) / 10
const totalFiber = computed(() => {
  const known = items.value.filter(i => i.fiber != null)
  return known.length ? fmtFiber(known.reduce((s, i) => s + (Number(i.fiber) || 0), 0)) : null
})

const dishWeight = computed(() => {
  const cw = Number(cookedWeight.value)
  return (cw > 0 ? cw : 0) || ingredientsTotalWeight.value || 1
})

const per100Kcal = computed(() => Math.round(totalKcal.value / dishWeight.value * 100))
const per100Protein = computed(() => Math.round(totalProtein.value / dishWeight.value * 100))
const per100Fat = computed(() => Math.round(totalFat.value / dishWeight.value * 100))
const per100Carbs = computed(() => Math.round(totalCarbs.value / dishWeight.value * 100))
const per100Fiber = computed(() =>
  totalFiber.value == null ? null : fmtFiber(totalFiber.value / dishWeight.value * 100))

function round1(v) { return Math.round((v || 0) * 10) / 10 }

const hasChanges = computed(() => {
  if (dishName.value !== originalName.value) return true
  if (cookedWeight.value !== originalWeight.value) return true
  if ((portionsCount.value || 0) !== (originalPortions.value || 0)) return true
  if (cookingMethod.value !== originalCookingMethod.value) return true
  if (items.value.length !== originalItems.value.length) return true
  return items.value.some((item, i) => {
    const orig = originalItems.value[i]
    return !orig || item.product_name !== orig.product_name || item.weight_g !== orig.weight_g || item.product_id !== orig.product_id
  })
})

function recalcItem(item) {
  if (!item.per100) return
  const w = item.weight_g || 0
  item.kcal = Math.round((item.per100.kcal || 0) * w / 100 * 10) / 10
  item.protein = Math.round((item.per100.protein || 0) * w / 100 * 10) / 10
  item.fat = Math.round((item.per100.fat || 0) * w / 100 * 10) / 10
  item.carbs = Math.round((item.per100.carbs || 0) * w / 100 * 10) / 10
  // Пустую клетчатку оставляем пустой: подстановка нуля выдала бы незнание
  // за измерение и закрыла бы путь к ИИ-оценке.
  item.fiber = item.per100.fiber == null
    ? null
    : Math.round(Number(item.per100.fiber) * w / 100 * 10) / 10
}

// ── Источник ──
function getSourceBadge(source) {
  if (!source) return null
  const s = source.toLowerCase()
  if (s.includes('скурихин') || s.includes('skurikhin')) return { label: 'Скурихин', color: '#2D6A4F' }
  if (s.includes('usda')) return { label: 'USDA', color: '#1A5A7A' }
  return null
}

// ── Редактирование названия ──
function startEditName() {
  editingName.value = true
  nextTick(() => nameInputRef.value?.focus())
}

// ── Редактирование веса блюда ──
function startEditWeight() {
  editingWeight.value = true
  nextTick(() => weightInputRef.value?.focus())
}

// Правка — это изменённое число, а не сам факт, что поле открыли. Раньше
// userEditedWeight поднимался в startEditWeight, и одного тапа хватало, чтобы
// вес навсегда отвязался от суммы: человек правил граммовки дальше, а в итогах
// оставалось прежнее число — и уезжало в дневник весом порции.
function onCookedWeightInput(e) {
  const val = parseDecimal(e.target.value)
  if (val !== cookedWeight.value) userEditedWeight.value = true
  cookedWeight.value = val
}

// Пустое поле — незаконченный ввод, а не «блюдо весит ноль»: возвращаем сумму
// ингредиентов и снова разрешаем ей вести вес за собой.
function finishEditWeight() {
  editingWeight.value = false
  if (!(cookedWeight.value > 0)) {
    userEditedWeight.value = false
    cookedWeight.value = Math.round(ingredientsTotalWeight.value) || 0
  }
}

// ── Редактирование веса ингредиента ──
function startEditItemWeight(idx) {
  items.value[idx]._editingWeight = true
  items.value[idx]._prevWeight = items.value[idx].weight_g
  nextTick(() => itemWeightRefs.value[idx]?.focus())
}

function finishEditItemWeight(idx) {
  const item = items.value[idx]
  item._editingWeight = false
  recalcItem(item)
}

// ── Удалить ингредиент ──
function removeItem(idx) {
  items.value.splice(idx, 1)
}

// ── Поиск продукта ──
function openSearchForItem(idx) {
  searchTarget.value = { type: 'replace', idx }
  searchModalQuery.value = items.value[idx].product_name
  showSearchModal.value = true
}

function openSearchForAdd() {
  searchTarget.value = { type: 'add' }
  searchModalQuery.value = ''
  showSearchModal.value = true
}

function onSearchSelect(product) {
  showSearchModal.value = false
  const per100 = {
    kcal: product.kcal != null ? parseFloat(product.kcal) : null,
    protein: product.protein != null ? parseFloat(product.protein) : null,
    fat: product.fat != null ? parseFloat(product.fat) : null,
    carbs: product.carbs != null ? parseFloat(product.carbs) : null,
  }

  if (searchTarget.value?.type === 'replace') {
    const idx = searchTarget.value.idx
    const item = items.value[idx]
    const w = item.weight_g || 100
    items.value[idx] = {
      ...item,
      product_name: product.name,
      product_id: product.id,
      db_product_name: product.name,
      product_source: product.source,
      product_nutrients: product.nutrients,
      per100,
      kcal: Math.round((per100.kcal || 0) * w / 100 * 10) / 10,
      protein: Math.round((per100.protein || 0) * w / 100 * 10) / 10,
      fat: Math.round((per100.fat || 0) * w / 100 * 10) / 10,
      carbs: Math.round((per100.carbs || 0) * w / 100 * 10) / 10,
    }
  } else if (searchTarget.value?.type === 'add') {
    const w = 100
    items.value.push({
      product_name: product.name,
      product_id: product.id,
      db_product_name: product.name,
      product_source: product.source,
      product_nutrients: product.nutrients,
      weight_g: w,
      per100,
      kcal: Math.round((per100.kcal || 0) * w / 100 * 10) / 10,
      protein: Math.round((per100.protein || 0) * w / 100 * 10) / 10,
      fat: Math.round((per100.fat || 0) * w / 100 * 10) / 10,
      carbs: Math.round((per100.carbs || 0) * w / 100 * 10) / 10,
      _editingWeight: false,
      _prevWeight: w,
    })
  }
}

// ── Избранное ──
async function toggleFavorite() {
  try {
    const { data } = await api.patch(`/diary/dishes/${dishId}/favorite`)
    isFavorite.value = data.is_favorite
  } catch (err) {
    console.error('Toggle favorite error:', err)
  }
}

// ── Текст рецепта ──
const recipeTextRef = ref(null)

function autoResizeRecipeText() {
  const el = recipeTextRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

async function saveRecipeText() {
  try {
    await api.patch(`/cooking/dish/${dishId}/recipe-text`, { text: recipeText.value })
  } catch (err) {
    console.error('Save recipe text error:', err)
  }
}

// ── Фото рецепта ──
function resizeAndEncode(file, maxPx) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      resolve(dataUrl.split(',')[1])
    }
    img.onerror = reject
    img.src = url
  })
}

async function onPhotoFile(e) {
  const file = e.target.files?.[0]
  if (!file) return
  e.target.value = ''

  // Показываем превью сразу
  photoUrl.value = URL.createObjectURL(file)
  uploadingPhoto.value = true

  try {
    const base64 = await resizeAndEncode(file, 1024)
    const { data } = await api.post('/diary/upload-photo', {
      base64,
      mimeType: file.type || 'image/jpeg',
    })
    photoUrl.value = data.url
    // Сохраняем фото в БД сразу
    await api.patch(`/cooking/dish/${dishId}`, { photo_url: data.url })
  } catch (err) {
    console.error('Photo upload error:', err)
  } finally {
    uploadingPhoto.value = false
  }
}

// ── Сохранить ──
async function saveChanges() {
  // Проверка: итоговый вес > суммы ингредиентов → предложить добавить воду
  if (waterDiff.value > 0) {
    showWaterPopup.value = true
    return
  }
  doSave()
}

async function addWaterAndSave() {
  showWaterPopup.value = false
  // Добавляем воду (product_id=100239, Скурихин) на разницу в весе
  const w = waterDiff.value
  items.value.push({
    product_name: 'Вода',
    product_id: 100239,
    db_product_name: 'Вода',
    product_source: null,
    product_nutrients: null,
    weight_g: w,
    // Клетчатки в воде ноль, и это измерение, а не незнание — так и пишем.
    per100: { kcal: 0, protein: 0, fat: 0, carbs: 0, fiber: 0 },
    kcal: 0, protein: 0, fat: 0, carbs: 0,
    fiber: 0, water_ml: w,
    _editingWeight: false,
    _prevWeight: w,
  })
  await nextTick()
  doSave()
}

async function doSave() {
  saving.value = true
  try {
    await api.patch(`/cooking/dish/${dishId}`, {
      name: dishName.value?.trim(),
      cooked_weight_g: cookedWeight.value || null,
      portions_count: portionsCount.value > 0 ? portionsCount.value : null,
      cooking_method: cookingMethod.value,
      items: items.value.map(i => ({
        product_id: i.product_id || null,
        product_name: i.product_name,
        weight_g: i.weight_g || 0,
        kcal: i.kcal || 0,
        protein: i.protein || 0,
        fat: i.fat || 0,
        carbs: i.carbs || 0,
        fiber: i.fiber || null,
        water_ml: i.water_ml || null,
        vitamins: i.vitamins || null,
        eaten_at: i.eaten_at || null,
      })),
    })
    isNewUnsaved.value = false
    router.push('/cooking')
  } catch (err) {
    console.error('Save error:', err)
    const detail = err.response?.data?.detail ? `\n(${err.response.data.detail})` : ''
    alert((err.response?.data?.error || 'Ошибка сохранения') + detail)
  } finally {
    saving.value = false
  }
}

// ── Удалить из рецептов ──
async function removeFromRecipes() {
  try {
    await api.delete(`/cooking/dish/${dishId}/from-recipes`)
    router.push('/cooking')
  } catch (err) {
    console.error('Remove error:', err)
  }
}

// ── Назад ──
async function goBack() {
  // Если рецепт был создан с нуля и ни разу не сохранён — удаляем из БД
  if (isNewUnsaved.value) {
    try { await api.delete(`/cooking/dish/${dishId}/from-recipes`) } catch {}
  }
  router.push('/cooking')
}
</script>

<style scoped>
.pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fiber   { background: #A6B779; border-radius: 8px; padding: 2px 8px; color: var(--ink); }

.pill-big {
  height: 28px;
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 14px;
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
}

.placeholder-recipe::placeholder {
  color: var(--ink-70);
}

/* Hide number input spinners */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
